import React from 'react';
import { deriveOverviewStage } from '../../utils/overviewStage';
import { trackEvent } from '../../analytics';

// The route into the Edits tab, as ONE component.
//
// Two cards on the Footage tab need it — CompiledVideoCard (a video this
// project compiled from photos) and FootageTab's SourceVideoCard (a video the
// user uploaded, or combined from their own clips) — and a route to the same
// place, drawn twice, is two places for the icon, the label rule and the
// geometry to drift apart. This is the single source of truth for all three.
//
// The LABEL is state-aware because the destination means two different things
// depending on whether a render already exists, and OverviewTab already draws
// exactly that distinction: it says "Fine-tune in Editor" on its
// `edit_complete` stage (there is a finished render, so the trip is about
// adjusting it) and "Open Editor" on `ready_to_edit` (there is no render yet,
// so the trip is about making one). Reusing `deriveOverviewStage` rather than
// re-deciding it here is what keeps the app saying ONE thing per state — the
// user should not meet a button on Footage that promises to "fine-tune" a
// render they have never made.

// The source video is raw material, not a finished piece — the next thing to
// do with it is edit it. Sliders rather than an arrow: it names the
// destination (the Edits tab's controls) instead of just pointing away from
// here.
const SLIDERS_ICON = (
    <svg
        className="pv-editor-cta-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <line x1="4" y1="21" x2="4" y2="14" />
        <line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" />
        <line x1="20" y1="12" x2="20" y2="3" />
        <line x1="1" y1="14" x2="7" y2="14" />
        <line x1="9" y1="8" x2="15" y2="8" />
        <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
);

// Exported for tests and for any host that needs the wording without the
// button. `deriveOverviewStage` is called with its defaults on purpose: the
// only stage this rule cares about is `edit_complete`, which is decided by
// `project.editedVideo.status` alone and is unaffected by the upload/analysis
// flags Overview passes.
// One rule, two consumers. The wording below and the `has_render` analytics
// dimension both mean the same thing — "there is a finished render to
// fine-tune" — so they are decided in one place rather than re-derived, which
// is how a label and the event describing it drift apart.
function hasFinishedRender(project) {
    return deriveOverviewStage(project) === 'edit_complete';
}

export function editorCtaLabel(project) {
    return hasFinishedRender(project) ? 'Fine-tune in Editor' : 'Open Editor';
}

const EditorCta = ({ project, onClick = null, testId }) => {
    // Not drawn rather than drawn dead. A host with no tab switcher has no
    // route to offer, and a button that names a destination it cannot reach is
    // worse than no button — the same rule CompiledVideoCard documents for its
    // `onOpenEdits` prop.
    if (typeof onClick !== 'function') return null;

    // The whole point of this button is to open a Footage → Edits path that
    // uploaded/combined projects never had, and an unmeasured path is one
    // nobody can tell worked: in GA4 it would look exactly like the state
    // before it shipped. Fired HERE, once, so both hosts are covered by
    // construction and neither can be wired up without it.
    //
    // `cta_click` + `cta_id` is the site-wide CTA taxonomy (LandingPage's
    // `hero_primary`, `funnel`, etc.). The two extra dimensions are the two
    // questions this button raises:
    //   card       — which of the two Footage cards was clicked (the compiled
    //                card has offered this route for a while; the source card
    //                is what this change added), so their pull can be compared.
    //                The test id doubles as that identity, which means renaming
    //                one is a taxonomy change, not just a test edit.
    //   has_render — which label the user actually saw ("Fine-tune in Editor"
    //                vs "Open Editor"); without it the two variants are
    //                indistinguishable in reporting.
    const handleClick = () => {
        trackEvent('cta_click', {
            cta_id: 'footage_open_editor',
            card: testId,
            has_render: hasFinishedRender(project),
        });
        onClick();
    };

    return (
        <button
            type="button"
            className="btn btn-teal pv-editor-cta"
            data-testid={testId}
            onClick={handleClick}
        >
            {SLIDERS_ICON}
            {editorCtaLabel(project)}
        </button>
    );
};

export default EditorCta;
